import { toLissenMediaCdnUrl } from "@libs/audio-publish-contracts";

/**
 * Anonymous-playable preview URL for a song, derived from
 * `catalog.songs.target_audio_url` (or any equivalent already-CDN URL). Callers
 * that serve unauthenticated clients — the public link page / LFP editor, and
 * the API on public song-serving surfaces — inject no CDN token, so whatever URL
 * this hands out must be publicly fetchable, and the two playback namespaces
 * differ on what that is:
 *
 * - Legacy `audio/<hash>/hls.m3u8`: the manifest itself is public and the CDN
 *   serves the preview window (first ~21 segments) without a token — the
 *   target URL doubles as the preview URL, unchanged historical behavior.
 * - On-demand `songs/<songId>/master.m3u8`: the master is token-gated
 *   (`songs_authorisation` edge rule), so handing it out would 403. The
 *   public artifact is the dedicated clip the publish/backfill pipeline
 *   writes alongside it — `songs/<songId>/preview/preview.m3u8`, exempted
 *   from token-auth by the `ondemand_preview_disable_auth` edge rule.
 *   Deriving by suffix-swap (not rebuilding from the song id) preserves any
 *   environment path prefix (`preview/pr-<n>/songs/…`) the URL carries.
 */
export function publicSongPreviewUrl(
  targetAudioUrl: string | null | undefined,
): string | null {
  const cdnUrl = toLissenMediaCdnUrl(targetAudioUrl) || null;
  if (!cdnUrl) return null;

  const MASTER_SUFFIX = "master.m3u8";
  if (cdnUrl.includes("/songs/") && cdnUrl.endsWith(`/${MASTER_SUFFIX}`)) {
    return `${cdnUrl.slice(0, -MASTER_SUFFIX.length)}preview/preview.m3u8`;
  }

  return cdnUrl;
}
